import React, { useEffect, useMemo, useState } from "react";
import { getBetHistory } from "../services/api/gameApi";

/* =========================================================
   HELPERS
========================================================= */

const isObject = (v) =>
  v !== null && typeof v === "object";

const scalar = (v, fallback = null) => {
  if (v === null || v === undefined) {
    return fallback;
  }

  if (!isObject(v)) {
    return v;
  }

  return (
    v.roundNumber ??
    v.value ??
    v._id ??
    v.id ??
    fallback
  );
};

const num = (v, fallback = 0) => {
  if (isObject(v)) {
    return num(
      v.amount ??
        v.value ??
        v.total,
      fallback
    );
  }

  const x = Number(v);

  return Number.isFinite(x)
    ? x
    : fallback;
};

const money = (v) =>
  num(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/* =========================================================
   EXTRACT API ROWS
========================================================= */

const extractRows = (payload) => {
  const data = payload?.data ?? payload;

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.records)) {
    return data.records;
  }

  if (Array.isArray(data?.bets)) {
    return data.bets;
  }

  if (Array.isArray(data?.items)) {
    return data.items;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  return [];
};

/* =========================================================
   NORMALIZE STATUS
========================================================= */

const getStatus = (row) => {
  const raw =
    row?.status ??
    row?.betStatus ??
    row?.result ??
    row?.state ??
    "";

  return String(
    scalar(raw, "")
  )
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
};

/* =========================================================
   NORMALIZE BET
========================================================= */

function normalize(row) {
  const amount = num(
    row?.amount ??
      row?.betAmount ??
      row?.stake ??
      row?.bet ??
      row?.stakeAmount
  );

  const statusRaw = getStatus(row);

  const multiplier = num(
    row?.cashoutMultiplier ??
      row?.cashOutMultiplier ??
      row?.exitMultiplier ??
      row?.multiplier ??
      row?.odds,
    0
  );

  let win = num(
    row?.winAmount ??
      row?.winningAmount ??
      row?.payout ??
      row?.returnAmount ??
      row?.cashoutAmount ??
      row?.cashOutAmount ??
      row?.totalPayout ??
      row?.amountWon,
    0
  );

  /* =======================================================
     EXPLICIT WIN STATUSES
  ======================================================= */

  const WIN_STATUSES = [
    "WON",
    "WIN",
    "WON_BET",
    "CASHED_OUT",
    "CASHOUT",
    "CASH_OUT",
    "SUCCESS",
    "COMPLETED_WIN",
  ];

  /* =======================================================
     EXPLICIT LOSS STATUSES
  ======================================================= */

  const LOSS_STATUSES = [
    "LOST",
    "LOSS",
    "LOST_BET",
    "FAILED",
    "CRASHED",
    "CRASH",
    "BUST",
    "COMPLETED_LOSS",
  ];

  /* =======================================================
     CHECK WIN
  ======================================================= */

  let won =
    WIN_STATUSES.includes(statusRaw) ||
    row?.cashedOut === true ||
    row?.cashout === true;

  /* =======================================================
     CHECK LOSS
  ======================================================= */

  let lost =
    LOSS_STATUSES.includes(statusRaw);

  /* =======================================================
     IMPORTANT:
     Agar explicit WIN aur LOSS dono somehow aaye,
     explicit status ko priority do.
  ======================================================= */

  if (
    WIN_STATUSES.includes(statusRaw)
  ) {
    won = true;
    lost = false;
  }

  if (
    LOSS_STATUSES.includes(statusRaw)
  ) {
    won = false;
    lost = true;
  }

  /* =======================================================
     FALLBACK:
     Agar API status nahi bhejti aur winAmount hai,
     tab WIN.
  ======================================================= */

  if (
    !won &&
    !lost &&
    win > 0
  ) {
    won = true;
  }

  /* =======================================================
     CALCULATE WIN AMOUNT
  ======================================================= */

  if (
    won &&
    !win &&
    multiplier > 0 &&
    amount > 0
  ) {
    win =
      amount *
      multiplier;
  }

  /* =======================================================
     ROUND
  ======================================================= */

  const round =
    scalar(
      row?.roundNumber,
      scalar(
        row?.round,
        scalar(
          row?.roundId,
          row?.gameRound ??
            row?.gameId ??
            "—"
        )
      )
    );

  /* =======================================================
     DATE
  ======================================================= */

  const created =
    scalar(
      row?.createdAt,
      scalar(
        row?.placedAt,
        scalar(
          row?.updatedAt,
          row?.date ??
            row?.timestamp ??
            null
        )
      )
    );

  /* =======================================================
     FINAL STATUS
  ======================================================= */

  let status = "PENDING";

  if (won) {
    status = "WIN";
  } else if (lost) {
    status = "LOSS";
  } else if (statusRaw) {
    status = statusRaw;
  }

  return {
    amount,
    multiplier,
    win,
    won,
    lost,
    status,
    round,
    created,
    original: row,
  };
}

/* =========================================================
   COMPONENT
========================================================= */

export default function BetHistory() {
  const [rows, setRows] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  /* =======================================================
     FILTER
  ======================================================= */

  const [historyFilter, setHistoryFilter] =
    useState("ALL");

  /* =======================================================
     PAGINATION
  ======================================================= */

  const [currentPage, setCurrentPage] =
    useState(1);

  const [itemsPerPage, setItemsPerPage] =
    useState(10);

  /* =======================================================
     LOAD HISTORY
  ======================================================= */

  useEffect(() => {
    let active = true;

    setLoading(true);
    setError("");

    getBetHistory(100)
      .then((response) => {
        if (!active) {
          return;
        }

        const extracted =
          extractRows(response);

        setRows(
          Array.isArray(extracted)
            ? extracted
            : []
        );

        setCurrentPage(1);
      })
      .catch((e) => {
        if (!active) {
          return;
        }

        setError(
          String(
            e?.message ||
              "Unable to load bet history."
          )
        );
      })
      .finally(() => {
        if (!active) {
          return;
        }

        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  /* =======================================================
     NORMALIZED DATA
  ======================================================= */

  const normalized = useMemo(() => {
    return rows.map(normalize);
  }, [rows]);

  /* =======================================================
     COUNTS
  ======================================================= */

  const totalBets =
    normalized.length;

  const totalWins =
    normalized.filter(
      (item) => item.won
    ).length;

  const totalLosses =
    normalized.filter(
      (item) => item.lost
    ).length;

  /* =======================================================
     NET RESULT
  ======================================================= */

  const netResult = useMemo(() => {
    return normalized.reduce(
      (sum, item) => {
        if (item.won) {
          return (
            sum +
            (
              item.win -
              item.amount
            )
          );
        }

        if (item.lost) {
          return (
            sum -
            item.amount
          );
        }

        return sum;
      },
      0
    );
  }, [normalized]);

  /* =======================================================
     FILTER DATA
  ======================================================= */

  const filteredRows = useMemo(() => {
    switch (historyFilter) {
      case "WIN":
        return normalized.filter(
          (item) =>
            item.won === true
        );

      case "LOSS":
        return normalized.filter(
          (item) =>
            item.lost === true
        );

      default:
        return normalized;
    }
  }, [
    normalized,
    historyFilter,
  ]);

  /* =======================================================
     FILTER COUNT
  ======================================================= */

  const filteredCount =
    filteredRows.length;

  /* =======================================================
     TOTAL PAGES
  ======================================================= */

  const totalPages =
    Math.max(
      1,
      Math.ceil(
        filteredCount /
          itemsPerPage
      )
    );

  /* =======================================================
     SAFE PAGE
  ======================================================= */

  const safeCurrentPage =
    Math.min(
      currentPage,
      totalPages
    );

  /* =======================================================
     PAGINATION SLICE
  ======================================================= */

  const startIndex =
    (safeCurrentPage - 1) *
    itemsPerPage;

  const endIndex =
    startIndex +
    itemsPerPage;

  const paginatedRows =
    filteredRows.slice(
      startIndex,
      endIndex
    );

  /* =======================================================
     FILTER CHANGE
  ======================================================= */

  const changeHistoryFilter = (
    filter
  ) => {
    setHistoryFilter(filter);
    setCurrentPage(1);
  };

  /* =======================================================
     PAGE CHANGE
  ======================================================= */

  const changePage = (
    page
  ) => {
    if (
      page < 1 ||
      page > totalPages ||
      page === safeCurrentPage
    ) {
      return;
    }

    setCurrentPage(page);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* =======================================================
     ROWS PER PAGE
  ======================================================= */

  const handleItemsPerPage = (
    event
  ) => {
    const value =
      Number(
        event.target.value
      );

    setItemsPerPage(
      value
    );

    setCurrentPage(1);
  };

  /* =======================================================
     PAGE NUMBERS
  ======================================================= */

  const pageNumbers =
    useMemo(() => {
      const pages = [];

      if (totalPages <= 7) {
        for (
          let i = 1;
          i <= totalPages;
          i++
        ) {
          pages.push(i);
        }

        return pages;
      }

      pages.push(1);

      if (
        safeCurrentPage > 4
      ) {
        pages.push("...");
      }

      const start = Math.max(
        2,
        safeCurrentPage - 1
      );

      const end = Math.min(
        totalPages - 1,
        safeCurrentPage + 1
      );

      for (
        let i = start;
        i <= end;
        i++
      ) {
        pages.push(i);
      }

      if (
        safeCurrentPage <
        totalPages - 3
      ) {
        pages.push("...");
      }

      pages.push(
        totalPages
      );

      return pages;
    }, [
      safeCurrentPage,
      totalPages,
    ]);

  /* =======================================================
     FILTER TITLE
  ======================================================= */

  const filterTitle =
    historyFilter === "ALL"
      ? "All Bets"
      : historyFilter === "WIN"
        ? "Winning Bets"
        : "Losing Bets";

  /* =======================================================
     EMPTY MESSAGE
  ======================================================= */

  const emptyMessage =
    historyFilter === "WIN"
      ? "No winning bets found."
      : historyFilter === "LOSS"
        ? "No losing bets found."
        : "No bets found.";

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="page-wrap">

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="page-heading">
{/* 
        <div className="eyebrow">
          ACCOUNT
        </div> */}

        <h1>
         My Bet History
        </h1>
{/* 
        <p>
          Track your Aviator bets,
          multipliers and Win /
          Loss results.
        </p> */}

      </div>

      {/* =====================================================
          ERROR
      ===================================================== */}

      {/* {error && (
        <div className="form-error">
          {error}
        </div>
      )} */}

      <section className="panel content-card">

        {/* ===================================================
            SUMMARY
        =================================================== */}

        {/* <div className="history-summary">
          <button
            type="button"
            className={`history-summary-card ${
              historyFilter === "ALL"
                ? "active"
                : ""
            }`}
            onClick={() =>
              changeHistoryFilter(
                "ALL"
              )
            }
          >
            <small>
              TOTAL BETS
            </small>

            <b>
              {totalBets}
            </b>
          </button>

          <button
            type="button"
            className={`history-summary-card ${
              historyFilter === "WIN"
                ? "active"
                : ""
            }`}
            onClick={() =>
              changeHistoryFilter(
                "WIN"
              )
            }
          >
            <small>
              WINS
            </small>

            <b className="win-text">
              {totalWins}
            </b>
          </button>

          <button
            type="button"
            className={`history-summary-card ${
              historyFilter === "LOSS"
                ? "active"
                : ""
            }`}
            onClick={() =>
              changeHistoryFilter(
                "LOSS"
              )
            }
          >
            <small>
              LOSSES
            </small>

            <b className="loss-text">
              {totalLosses}
            </b>
          </button>

          <div className="history-summary-card net-card">

            <small>
              NET RESULT
            </small>

            <b
              className={
                netResult >= 0
                  ? "win-text"
                  : "loss-text"
              }
            >
              {netResult >= 0
                ? "+"
                : "-"}
              ₹
              {money(
                Math.abs(
                  netResult
                )
              )}
            </b>

          </div>

        </div> */}

        {/* ===================================================
            ACTIVE FILTER
        =================================================== */}

        {/* <div className="history-filter-label">

          <span>
            Showing:
          </span>

          <strong>    
            {filterTitle}
          </strong>

        </div> */}

        {/* ===================================================
            TOOLBAR
        =================================================== */}

        {/* {!loading &&
          filteredCount > 0 && (   
            <div className="history-toolbar">

              <div className="history-result-count">

                Showing{" "}

                <b>
                  {startIndex + 1}
                </b>

                {" - "}

                <b>
                  {Math.min(
                    endIndex,
                    filteredCount
                  )}
                </b>

                {" of "}

                <b>
                  {filteredCount}
                </b>

                {" "}

                {historyFilter ===
                "WIN"
                  ? "winning bets"
                  : historyFilter ===
                      "LOSS"
                    ? "losing bets"
                    : "bets"}

              </div>

              <div className="history-per-page">

                <span>
                  Rows:
                </span>

                <select
                  value={
                    itemsPerPage
                  }
                  onChange={
                    handleItemsPerPage
                  }
                >
                  <option value={10}>
                    10
                  </option>

                  <option value={20}>
                    20
                  </option>

                  <option value={50}>
                    50
                  </option>
                </select>

              </div>

            </div>
          )} */}

        {/* ===================================================
            TABLE
        =================================================== */}

        <div className="table-scroll">

          <table>

            <thead>
              <tr>

                <th>
                  ROUND
                </th>

                <th>
                  DATE
                </th>

                <th>
                  BET
                </th>

                <th>
                  MULTIPLIER
                </th>

                <th>
                  WIN / LOSS
                </th>

                <th>
                  RESULT
                </th>

              </tr>
            </thead>

            <tbody>

              {/* LOADING */}

              {loading && (
                <tr>
                  <td
                    colSpan="6"
                    className="history-empty"
                  >
                    Loading…
                  </td>
                </tr>
              )}

              {/* EMPTY */}

              {!loading &&
                filteredCount === 0 && (
                  <tr>
                    <td
                      colSpan="6"
                      className="history-empty"
                    >
                      {emptyMessage}
                    </td>
                  </tr>
                )}

              {/* DATA */}

              {!loading &&
                paginatedRows.map(
                  (r, i) => {

                    const d =
                      r.created
                        ? new Date(
                            r.created
                          )
                        : null;

                    const valid =
                      d &&
                      !Number.isNaN(
                        d.getTime()
                      );

                    return (
                      <tr
                        key={`${String(
                          r.round
                        )}-${startIndex + i}`}
                      >

                        {/* ROUND */}

                        <td>
                          #
                          {String(
                            r.round
                          )}
                        </td>

                        {/* DATE */}

                        <td>
                          {valid
                            ? d.toLocaleString(
                                "en-IN"
                              )
                            : "—"}
                        </td>

                        {/* BET */}

                        <td>
                          ₹
                          {money(
                            r.amount
                          )}
                        </td>

                        {/* MULTIPLIER */}

                        <td>
                          {r.multiplier >
                          0
                            ? `${r.multiplier.toFixed(
                                2
                              )}x`
                            : "—"}
                        </td>

                        {/* WIN LOSS */}

                        <td
                          className={
                            r.won
                              ? "win"
                              : r.lost
                                ? "loss"
                                : ""
                          }
                        >

                          {r.won
                            ? `+₹${money(
                                r.win
                              )}`
                            : r.lost
                              ? `-₹${money(
                                  r.amount
                                )}`
                              : "—"}

                        </td>

                        {/* RESULT */}

                        <td>

                          <span
                            className={`result-pill ${
                              r.won
                                ? "win"
                                : r.lost
                                  ? "loss"
                                  : "pending"
                            }`}
                          >
                            {r.status}
                          </span>

                        </td>

                      </tr>
                    );
                  }
                )}

            </tbody>

          </table>

        </div>

        {/* ===================================================
            PAGINATION
        =================================================== */}

        {!loading &&
          filteredCount > 0 &&
          totalPages > 1 && (

            <div className="history-pagination">

              {/* PREVIOUS */}

              <button
                type="button"
                className="pagination-arrow"
                disabled={
                  safeCurrentPage ===
                  1
                }
                onClick={() =>
                  changePage(
                    safeCurrentPage -
                      1
                  )
                }
              >
                ‹
              </button>

              {/* PAGE NUMBERS */}

              {pageNumbers.map(
                (page, index) => {

                  if (
                    page === "..."
                  ) {
                    return (
                      <span
                        key={`dots-${index}`}
                        className="pagination-dots"
                      >
                        ...
                      </span>
                    );
                  }

                  return (
                    <button
                      key={page}
                      type="button"
                      className={
                        safeCurrentPage ===
                        page
                          ? "active"
                          : ""
                      }
                      onClick={() =>
                        changePage(
                          page
                        )
                      }
                    >
                      {page}
                    </button>
                  );
                }
              )}

              {/* NEXT */}

              <button
                type="button"
                className="pagination-arrow"
                disabled={
                  safeCurrentPage ===
                  totalPages
                }
                onClick={() =>
                  changePage(
                    safeCurrentPage +
                      1
                  )
                }
              >
                ›
              </button>

            </div>
          )}

      </section>
    </div>
  );
}